import type { PacketResult } from "../types/shadow-run";
import { formatPayPeriod } from "./formatters";

export interface PayPeriodGroup {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  packets: PacketResult[];
}

export interface PayGroupSection {
  code: string;
  name: string;
  periods: PayPeriodGroup[];
}

/**
 * Groups a flat array of PacketResult into PayGroup -> PayPeriod hierarchy.
 *
 * - Pay groups are sorted alphabetically by name
 * - Pay periods within each group are sorted chronologically by start date
 * - Returns an empty array if no packets have grouping fields
 */
export function groupPackets(packets: PacketResult[]): PayGroupSection[] {
  // Check whether any packet in the array has grouping fields.
  // A packet is groupable if it has a period and at least a pay group code or name.
  const hasGroupingFields = packets.some(
    (p) => (p.pay_group_code || p.pay_group_name) && p.pay_period_start,
  );
  if (packets.length === 0 || !hasGroupingFields) {
    return [];
  }

  const groupMap = new Map<
    string,
    {
      code: string;
      name: string;
      periodMap: Map<
        string,
        { start: string; end: string; packets: PacketResult[] }
      >;
    }
  >();

  for (const packet of packets) {
    // Skip packets that lack any period information — we can't place them in
    // the hierarchy at all. Packets that have a period but no pay_group_code
    // are placed under their pay_group_name (or "Other" as a last resort).
    if (!packet.pay_period_start) {
      continue;
    }

    const code = packet.pay_group_code ?? packet.pay_group_name ?? "Other";
    const name = packet.pay_group_name ?? packet.pay_group_code ?? "Other";
    const periodStart = packet.pay_period_start;
    const periodEnd = packet.pay_period_end ?? "";
    const periodKey = `${periodStart}|${periodEnd}`;

    let group = groupMap.get(code);
    if (!group) {
      group = { code, name, periodMap: new Map() };
      groupMap.set(code, group);
    }

    let period = group.periodMap.get(periodKey);
    if (!period) {
      period = { start: periodStart, end: periodEnd, packets: [] };
      group.periodMap.set(periodKey, period);
    }

    period.packets.push(packet);
  }

  const sections: PayGroupSection[] = [];

  for (const group of groupMap.values()) {
    const periods: PayPeriodGroup[] = [];

    for (const period of group.periodMap.values()) {
      periods.push({
        periodStart: period.start,
        periodEnd: period.end,
        periodLabel: formatPayPeriod(period.start, period.end),
        packets: period.packets,
      });
    }

    // Sort periods chronologically by start date.
    // ISO date strings (YYYY-MM-DD) sort correctly as strings.
    periods.sort((a, b) => a.periodStart.localeCompare(b.periodStart));

    sections.push({
      code: group.code,
      name: group.name,
      periods,
    });
  }

  // Sort pay groups alphabetically by name
  sections.sort((a, b) => a.name.localeCompare(b.name));

  return sections;
}
